import { faRightToBracket, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Input } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import colors from '../../foundation/assets/colors';
import Logo from '../../foundation/assets/svgs/Logo';
import {
  isSafeRedirectTarget,
  LOGIN_REDIRECT_PARAM,
} from '../../foundation/utils/loginRedirect';
import Login from '../authentication/login/Login';
import { selectUsername } from '../authentication/redux/selectors';
import ImprovedSignupModal from '../authentication/signup/ImprovedSignupModal';

const Landing = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoginModalVisible, setLoginModalVisibility] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isSignupModalVisible, setSignupModalVisible] = useState(false);

  const username = useSelector(selectUsername);

  // Auto-open login modal when redirected with ?login=1 and user is not logged in.
  // Use selectUsername (synchronous Redux slice) instead of selectUser (RTK Query
  // cache) to avoid a race condition where the cache hasn't populated yet after
  // Startup.tsx restores the session, causing the login modal to appear even though
  // the user is already authenticated.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('login') === '1' && !username) {
      setLoginModalVisibility(true);
    }
  }, [location.search, username]);

  // Handles search button click
  const handleSearchBtnClick = () => {
    navigate(`/variant-check${searchValue ? `?search=${searchValue}` : ''}`);
  };

  const handleLoginClick = () => {
    setLoginModalVisibility(true);
  };

  const handleSignupClick = () => {
    setSignupModalVisible(true);
  };

  // Forward to the originally requested deep link once login succeeded.
  const handleLoginSuccess = () => {
    const target = new URLSearchParams(location.search).get(
      LOGIN_REDIRECT_PARAM,
    );
    if (isSafeRedirectTarget(target)) {
      navigate(target, { replace: true });
    }
  };

  const onLoginModalClose = () => {
    setLoginModalVisibility(false);
  };

  const onSignupModalClose = () => {
    setSignupModalVisible(false);
  };

  return (
    <div className="l-landing">
      {isLoginModalVisible && (
        <Login
          onClose={onLoginModalClose}
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      {isSignupModalVisible && (
        <ImprovedSignupModal onClose={onSignupModalClose} />
      )}
      {!username && (
        <div className="l-landing__header">
          <Button
            className="l-landing__header-btn"
            variant="outlined"
            shape="round"
            onClick={handleLoginClick}
            icon={<FontAwesomeIcon icon={faUser} color={colors.primary} />}
          >
            Login
          </Button>
          <Button
            className="l-landing__header-btn"
            variant="outlined"
            shape="round"
            onClick={handleSignupClick}
            icon={
              <FontAwesomeIcon icon={faRightToBracket} color={colors.primary} />
            }
          >
            Signup
          </Button>
        </div>
      )}
      <div className="l-landing__sub-header">
        <div className="l-landing__sub-header-logo">
          <Logo />
        </div>
        <div className="l-landing__sub-header-headline">
          A Genetic Information Management System (GIMS)
          <br />
          for transparently traceable DNA Variant Interpretation
        </div>
      </div>
      <div className="l-landing__content">
        <div className="l-landing__search-wrapper">
          <Input
            className="l-landing__search-input"
            placeholder="Enter variant or gene"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchValue(e.target.value)
            }
            value={searchValue}
          />
          <Button
            className="l-landing__search-btn"
            onClick={handleSearchBtnClick}
          >
            Search
          </Button>
        </div>
        <div className="l-landing__search-result-wrapper">
          <span>Example</span>
          <span>BRCA1:c.5431C&gt;T</span>
        </div>
      </div>
      <div
        className="l-landing__footer-links"
        style={{
          position: 'absolute',
          bottom: '24px',
          left: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <a
          onClick={() => navigate('/legal#legal-notice')}
          style={{ fontSize: '12pt', color: '#7A9DAE', cursor: 'pointer' }}
        >
          Legal notice
        </a>
        <a
          onClick={() => navigate('/legal#privacy-policy')}
          style={{ fontSize: '12pt', color: '#7A9DAE', cursor: 'pointer' }}
        >
          Privacy policy
        </a>
        <a
          onClick={() => navigate('/legal#disclaimer')}
          style={{ fontSize: '12pt', color: '#7A9DAE', cursor: 'pointer' }}
        >
          Disclaimer
        </a>
      </div>
    </div>
  );
};

export default Landing;
